import { generateShareLink, buildCanonicalShareUrl } from '@/api/share';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_SHARE_CLICKED,
  EVENT_SHARED_PUBLICLY,
  type ShareContentType,
  type ShareModalEntry,
} from '@/lib/analyticsEvents';
import { getViewerAnalyticsProps, type ShareLinkType } from '@/lib/contentPageAnalytics';
import { isDesktop } from '@/utils/isDesktop';
import { debug, error as logError } from '@/utils/logger';
import { isNativePlatform } from '@/lib/platform';

function isNotAllowedShareError(err: unknown): boolean {
  const e = err as { name?: string; message?: string };
  return (
    e?.name === 'NotAllowedError'
    || /user gesture|not allowed/i.test(e?.message ?? '')
  );
}

/** Web Share API requires transient user activation; Capacitor Share does not. */
function canInvokeWebShare(): boolean {
  if (isNativePlatform()) return true;
  if (typeof navigator === 'undefined') return false;
  const activation = (navigator as Navigator & { userActivation?: { isActive?: boolean } }).userActivation;
  if (!activation) return true;
  return activation.isActive;
}

class ShareAbortError extends Error {
  override name = 'AbortError';
}

/**
 * Open the platform-native share sheet. On native (Capacitor), routes through
 * @capacitor/share which uses UIActivityViewController / Android's share intent.
 * On web, uses navigator.share which surfaces the Web Share API where
 * available (iOS/Android Safari, Chrome). Returns once the user has dismissed
 * or completed the sheet; throws an Error with name === 'AbortError' if the
 * user cancelled.
 */
/** Share a URL via Capacitor Share (native) or Web Share API. */
export async function shareUrlViaPlatform(url: string): Promise<void> {
  if (isNativePlatform()) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ url });
      return;
    } catch (e) {
      const err = e as { message?: string; errorMessage?: string };
      const message = err?.message ?? err?.errorMessage ?? '';
      if (/cancel|dismiss|aborted/i.test(message)) {
        throw new ShareAbortError(message || 'Share dismissed');
      }
      throw e;
    }
  }
  if (typeof navigator === 'undefined' || !navigator.share) {
    throw new Error('navigator.share unavailable');
  }
  await navigator.share({ url });
}

export type OpenEditorShareModalFn = (opts?: { prefetchedShareUrl?: string }) => void;

/**
 * Opens the system share sheet (`navigator.share`) for mobile list, place, and
 * itinerary editor flows, with a full fallback to the in-app ShareModal.
 *
 * Flow:
 *  - Desktop → opens `openShareModal` immediately.
 *  - Mobile with `navigator.share` available →
 *      1. Generates a share link.
 *      2. Fires `EVENT_SHARED_PUBLICLY` (link created).
 *      3. Calls `navigator.share({ url })`.
 *      4a. On success → fires `EVENT_SHARE_CLICKED` (native_share) and returns.
 *      4b. On `AbortError` (user dismissed the sheet) → returns without opening the modal.
 *      4c. On other `navigator.share` errors → calls `openShareModal` with the
 *          prefetched URL so ShareModal skips link re-generation.
 *  - Mobile with no `navigator.share`, or link generation fails → opens `openShareModal`.
 *
 * @param options.postId        - ID of the post being shared.
 * @param options.contentType   - Content type for analytics (`list` | `place` | `itinerary`).
 * @param options.entry         - How the share action was triggered (for analytics).
 * @param options.openShareModal - Callback to open the in-app ShareModal; receives an
 *                                 optional `prefetchedShareUrl` to avoid duplicate link
 *                                 generation when the helper already obtained a URL.
 */
export async function openMobileEditorShareNativeFirstOrModal(options: {
  postId: string;
  contentType: ShareContentType;
  openShareModal: OpenEditorShareModalFn;
  entry: ShareModalEntry;
  /** When true, use canonical OPEN-browse URLs if tokenized share generation is unavailable. */
  guestOpenBrowseFallback?: boolean;
}): Promise<void> {
  const { postId, contentType, openShareModal, entry, guestOpenBrowseFallback = false } = options;
  const resourceType = 'post' as const;

  const openGuestCanonicalShare = () => {
    const canonicalUrl = buildCanonicalShareUrl(resourceType, postId, { contentType });
    const viewer = getViewerAnalyticsProps({ hasUser: false });
    WebAnalytics.trackContentInteraction(EVENT_SHARED_PUBLICLY, {
      resourceType,
      resourceId: postId,
      content_type: contentType,
      share_link_type: 'canonical' satisfies ShareLinkType,
      ...viewer,
      entry,
    });
    if (isDesktop()) {
      openShareModal({ prefetchedShareUrl: canonicalUrl });
      return;
    }
    if (canInvokeWebShare()) {
      void shareUrlViaPlatform(canonicalUrl).catch((shareErr: unknown) => {
        const err = shareErr as { name?: string };
        if (err?.name === 'AbortError') return;
        openShareModal({ prefetchedShareUrl: canonicalUrl });
      });
      return;
    }
    openShareModal({ prefetchedShareUrl: canonicalUrl });
  };

  WebAnalytics.trackContentInteraction(EVENT_SHARE_CLICKED, {
    resourceType,
    resourceId: postId,
    content_type: contentType,
    share_method: isDesktop() ? 'modal' : 'native_first_editor',
    share_link_type: (guestOpenBrowseFallback ? 'canonical' : 'tokenized') satisfies ShareLinkType,
    ...getViewerAnalyticsProps({ hasUser: !guestOpenBrowseFallback }),
    entry,
  });

  if (isDesktop()) {
    if (guestOpenBrowseFallback) {
      openGuestCanonicalShare();
      return;
    }
    openShareModal();
    return;
  }

  const canShareNatively = isNativePlatform() || (typeof navigator !== 'undefined' && !!navigator.share);

  if (canShareNatively) {
    try {
      const response = await generateShareLink({
        resource_type: 'post',
        resource_id: postId,
      });

      try {
        WebAnalytics.trackContentInteraction(EVENT_SHARED_PUBLICLY, {
          resourceType,
          resourceId: postId,
          content_type: contentType,
          share_token: response.share_token,
          share_link_type: 'tokenized' satisfies ShareLinkType,
          ...getViewerAnalyticsProps({ hasUser: !guestOpenBrowseFallback }),
          ...(response.created_by && { creator_id: response.created_by }),
        });
      } catch (analyticsErr) {
        logError('[editorMobileShare] Analytics failed (link still valid)', analyticsErr);
      }

      if (!canInvokeWebShare()) {
        debug('[editorMobileShare] web share skipped (no user activation), opening modal');
        openShareModal({ prefetchedShareUrl: response.share_url });
        return;
      }

      try {
        await shareUrlViaPlatform(response.share_url);
      } catch (shareErr: unknown) {
        const err = shareErr as { name?: string };
        if (err?.name === 'AbortError') {
          return;
        }
        if (isNotAllowedShareError(shareErr)) {
          debug('[editorMobileShare] web share blocked (user gesture expired), opening modal');
          openShareModal({ prefetchedShareUrl: response.share_url });
          return;
        }
        logError('[editorMobileShare] platform share failed, opening modal', shareErr);
        openShareModal({ prefetchedShareUrl: response.share_url });
        return;
      }

      WebAnalytics.trackContentInteraction(EVENT_SHARE_CLICKED, {
        resourceType,
        resourceId: postId,
        content_type: contentType,
        share_method: 'native_share',
        entry,
      });
      return;
    } catch (err: unknown) {
      logError('[editorMobileShare] generateShareLink failed, opening modal', err);
      if (guestOpenBrowseFallback) {
        openGuestCanonicalShare();
        return;
      }
    }
  }

  if (guestOpenBrowseFallback) {
    openGuestCanonicalShare();
    return;
  }

  openShareModal();
}
